// One identity-bound ready prompt. A replacement/cancellation retires every old callback.
export interface AttendancePromptIdentity {
    campaignId: string;
    sessionId: string;
    casterId: string;
    sessionEnd: string;
    deadline: number | null;
}

export interface AttendancePrompt {
    identity: AttendancePromptIdentity;
    update(identity: AttendancePromptIdentity): void;
    close(): void;
}

export function createAttendancePrompt(identity: AttendancePromptIdentity, options: {
    endpoint: string;
    now(): number;
    confirmed(identity: AttendancePromptIdentity): void;
    dismissed(): void;
}): AttendancePrompt {
    let retired = false;
    let pending = false;
    let current = identity;
    const modalEl = document.getElementById('campaignSessionReadyModal');
    const modal = modalEl && typeof bootstrap !== 'undefined' && bootstrap.Modal
        ? bootstrap.Modal.getOrCreateInstance(modalEl) : null;
    const root = modal ? modalEl! : document.createElement('div');
    if (!modal) {
        root.id = 'campaignPopup';
        root.style.cssText = 'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:white;color:#212529;padding:24px;border-radius:8px;width:min(92vw,320px);text-align:center;z-index:20030';
        root.setAttribute('role', 'dialog');
        root.setAttribute('aria-label', 'Ready to play');
        root.innerHTML = '<p>Are you ready to play?</p><p data-ready-countdown></p><p data-ready-error role="alert" hidden></p><button id="playButton" type="button" class="btn btn-primary">Yes, I’m ready!</button>';
    }
    // No animation gap in this mandatory prompt: a cancelled/replaced session must hide now,
    // including a change arriving while the original modal would still be fading in or out.
    root.classList.remove('fade');
    if (root.parentElement !== document.body) document.body.appendChild(root);
    const button = root.querySelector<HTMLButtonElement>('#campaignSessionReadyConfirmBtn, #playButton')!;
    const countdown = root.querySelector<HTMLElement>('#campaignSessionReadyCountdown, [data-ready-countdown]');
    const errorEl = root.querySelector<HTMLElement>('#campaignSessionReadyError, [data-ready-error]');
    const dismissButtons = root.querySelectorAll<HTMLButtonElement>('[data-bs-dismiss]');
    const setPending = (value: boolean): void => {
        pending = value;
        button.disabled = value;
        dismissButtons.forEach(dismiss => { dismiss.disabled = value; });
    };
    const paint = (): void => {
        if (!countdown) return;
        countdown.hidden = current.deadline === null;
        if (current.deadline === null) return;
        const seconds = Math.max(0, Math.ceil((current.deadline - options.now()) / 1000));
        countdown.textContent = seconds > 0
            ? `Confirm within ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')} to stay with the party.`
            : 'Ready check has ended. Confirm to rejoin the party.';
    };
    const timer = window.setInterval(paint, 1000);
    const onHide = (event: Event): void => { if (pending && !retired) event.preventDefault(); };
    const onHidden = (): void => { prompt.close(); options.dismissed(); };
    const onConfirm = async (): Promise<void> => {
        if (pending || retired) return;
        setPending(true);
        if (errorEl) errorEl.hidden = true;
        const submitted = { ...current };
        try {
            const response = await fetch(options.endpoint, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin',
                body: JSON.stringify({ campaign_id: submitted.campaignId, campaign_session_id: submitted.sessionId, caster_id: submitted.casterId }),
                signal: AbortSignal.timeout(20000),
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const result = await response.json() as { status?: string };
            if (result.status !== 'success') throw new Error('Attendance not confirmed');
            if (retired) return;
            options.confirmed(submitted);
            prompt.close();
        } catch {
            if (retired) return;
            if (errorEl) {
                errorEl.textContent = 'Could not confirm attendance. Please try again.';
                errorEl.hidden = false;
            }
        } finally {
            if (!retired) setPending(false);
        }
    };
    const prompt: AttendancePrompt = {
        identity,
        update(next) { current = next; prompt.identity = next; paint(); },
        close() {
            if (retired) return;
            retired = true;
            window.clearInterval(timer);
            button.removeEventListener('click', onConfirm);
            root.removeEventListener('hide.bs.modal', onHide);
            root.removeEventListener('hidden.bs.modal', onHidden);
            setPending(false);
            if (modal) modal.hide(); else root.remove();
        },
    };
    if (errorEl) errorEl.hidden = true;
    setPending(false);
    paint();
    button.addEventListener('click', onConfirm);
    root.addEventListener('hide.bs.modal', onHide);
    root.addEventListener('hidden.bs.modal', onHidden);
    if (modal) modal.show();
    return prompt;
}
